import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { CtaFooter } from '../../components/ai/CtaFooter';
import { getCMSPage, CMSPageContent } from '../../services/api';

/**
 * intrnauts.ai About — essay shape per sketch 003 Variant A.
 *
 * CMS-overridable:
 * - Scalar `headline` → masthead h1
 * - Scalar `intro`    → stand-first
 * - Scalar `mission`  → essay body (sanitized HTML replaces the locked essay)
 *
 * Hardcoded studio identity (per CONTEXT.md):
 * - Eyebrow text
 * - Sticky right-margin metadata (4 sections)
 * - Locked essay copy (when `mission` is empty)
 * - Team-card content + gradient avatar initials
 */

interface PersonCard { initials: string; name: string; role: string; bio: string; }

const TEAM: PersonCard[] = [
  {
    initials: 'MH',
    name: 'Manzoor',
    role: 'Engineering · Founder',
    bio: 'Twenty-plus years of building production systems. Currently spending most of his thinking time on evals and observability for AI features.',
  },
  {
    initials: '+1',
    name: 'Engineering partner',
    role: 'Full-stack · AI',
    bio: 'The person who turns the eval set into an actual product. Frontend, backend, the AI surface, the deploy pipeline — comfortable across the whole thing.',
  },
  {
    initials: '+1',
    name: 'Design partner',
    role: 'Product · Systems',
    bio: 'Translates what the model can do into something a person actually wants to use. Spends as much time on copy as on pixels.',
  },
];

const LOCKED_ESSAY = (
  <>
    <p>Intrnauts began as a question that wouldn't go away: <em>can you build AI that survives contact with real work?</em> Not benchmarks. Not demos. The unglamorous version — software people actually use on the worst Tuesday of the quarter, that still does the right thing when the prompt is ambiguous and the data is a mess.</p>
    <p>The honest answer is: only if you treat AI like every other engineering problem. Evals first. Observability built in. Failure modes named out loud and tested for. Boring infrastructure that earns trust over months, not pitch decks that earn applause in fifteen minutes.</p>

    <h2>What we believe.</h2>
    <blockquote>We measure our work by whether it <i>made it into someone's day</i>, not by how it landed on stage.</blockquote>
    <p>It's tempting to mistake novelty for usefulness. A clever architecture, a surprising prompt, a model nobody else is using. Sometimes those things matter. More often the thing that actually shifts the metric is the unsexy work — the regression suite, the fallback path, the third pass over the rubric that revealed what the user actually wanted.</p>

    <h2>How we work.</h2>
    <p>We pick the smallest problem where AI is the actual unblocker — not the marketing layer. We define what good looks like before we write the first prompt. We ship the smallest thing that moves the evaluation score, then iterate against it weekly. We write down what we learn so the next team doesn't have to relearn it.</p>
    <blockquote>Craft is a <i>discipline</i>. Openness is a <i>practice</i>. Simplicity is a <i>decision</i>.</blockquote>
    <p>These three words are the closest we come to having a company motto. They sit above the whiteboard in the studio. They show up in code review when somebody starts building the cathedral. They are the questions we ask before merging a PR: is this the simplest version of itself? Did we share what we learned? Are we still proud of how it's made?</p>

    <h2>Where we go from here.</h2>
    <p>AI is changing what software looks like fast enough that anyone claiming to know the full picture is selling something. What we know is the next decade of products will be built differently — and we want to be one of the small group of teams that figures out <em>how</em>, in public, with our names on the work.</p>
  </>
);

const AboutAi: React.FC = () => {
  const [content, setContent] = useState<CMSPageContent | null>(null);

  useEffect(() => {
    getCMSPage('about').then(res => {
      if (res.data) setContent(res.data);
    });
  }, []);

  const fields = content?.fields ?? {};
  const cmsMission = fields.mission?.trim();

  return (
    <>
      <section className="ai-about-masthead">
        <div className="eyebrow">● About — who we are, how we think</div>
        <h1 className="ai-about-h1">
          {fields.headline ? (
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fields.headline) }} />
          ) : (
            <>A studio of <i>few</i>, working on AI that earns its keep.</>
          )}
        </h1>
        <p className="ai-about-standfirst">
          {fields.intro ?? "We're a small group of engineers and designers building useful AI systems for people who need their software to actually work tomorrow morning."}
        </p>
      </section>

      <section className="ai-essay">
        <article className="ai-essay-col">
          {cmsMission
            ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cmsMission) }} />
            : LOCKED_ESSAY}
        </article>

        <aside className="ai-margin" aria-label="Studio metadata">
          <section>
            <div className="k">— Studio</div>
            <div className="v">
              <p>Independent. Self-funded.</p>
              <p>Working in AI since 2019.</p>
            </div>
          </section>
          <section>
            <div className="k">— Location</div>
            <div className="v">
              <p>San Francisco · Remote-friendly</p>
              <p>UTC -8 · responding within 1 business day</p>
            </div>
          </section>
          <section>
            <div className="k">— Currently</div>
            <div className="v">
              <p>Open to 1–2 new engagements for Q3.</p>
              <p><Link to="/contact">Book intro call →</Link></p>
            </div>
          </section>
          <section>
            <div className="k">— Elsewhere</div>
            <div className="v">
              <p>Field notes · GitHub</p>
              <p>LinkedIn · Talks</p>
            </div>
          </section>
        </aside>
      </section>

      <section className="ai-people" aria-labelledby="ai-people-label">
        <h3 id="ai-people-label" className="ai-people-label">— The people, briefly</h3>
        <div className="ai-people-grid">
          {TEAM.map(p => (
            <article key={p.name} className="ai-person">
              <div className="ai-person-avatar" aria-hidden="true">{p.initials}</div>
              <h4>{p.name}</h4>
              <div className="ai-person-role">{p.role}</div>
              <p>{p.bio}</p>
            </article>
          ))}
        </div>
      </section>

      <CtaFooter headline={<>Want to work with us <i>on something hard?</i></>} />
    </>
  );
};

export default AboutAi;
